import { detectUploadFileKind, readFileHead } from '../../../../utils/uploadFileKind';
import { readRmcplotsExcelRows } from './rmcplotsExcelReader';

export interface ParsedReading {
  sampleInt: number;
  horizontalDisplacement: number;
  horizontalLoad: number;
  verticalDisplacement: number;
  verticalLoad: number;
  /** Machine seating-phase row (leading rows with displacement <= 0 while
   *  load ramps from 0). Kept since 2026-07-17 (mig 108) for the stress-path
   *  0-start ramp; excluded from calcs/analysis/displacement plots. */
  isSeating?: boolean;
}

/** One machine sample row as read from the source file, before any cleanup. */
export interface RawRmcplotsRow {
  intIndex: number;
  reading: ParsedReading;
}

export interface ParsedFileResult {
  stageNumber: number | null;
  readings: ParsedReading[];
  fileName: string;
  /** Source format the readings were extracted from. */
  sourceKind: RmcplotsSourceKind;
  /** The machine Int index where real data started (first row with Item0 > 0) */
  startIntIndex: number;
  /** Total sample rows in the source file before stripping */
  totalRows: number;
  /** All-zero rows dropped at the start (no load data at all) */
  leadingRowsSkipped: number;
  /** Seating-phase rows KEPT and flagged (displacement <= 0, load present) */
  seatingRowsKept: number;
  /** Trailing rows stripped (zero rows + machine retraction rows) */
  trailingZerosStripped: number;
  /** Trailing rows stripped because HorizontalLoad <= 0 (machine retraction) */
  trailingRetractionStripped: number;
}

/**
 * Which reader handles an uploaded result file (2026-09-29).
 *  - `xml`   — the native `.rmcplots` file written by the RMC150 controller.
 *  - `excel` — the same file opened in Excel and saved as a workbook (.xlsx /
 *              .xlsm). Excel flattens the XML into one sheet whose header row
 *              carries `Int`, `Item0` … `Item4`; only the column source
 *              differs, every cleanup rule below is shared.
 */
export type RmcplotsSourceKind = 'xml' | 'excel';

/** Extensions the Result Entry drop zone accepts (also the `<input accept>` list). */
export const RMCPLOTS_UPLOAD_EXTENSIONS = ['.rmcplots', '.xlsx', '.xlsm'] as const;
export const RMCPLOTS_UPLOAD_ACCEPT = RMCPLOTS_UPLOAD_EXTENSIONS.join(',');

const XML_EXT = /\.(rmcplots|xml)$/i;
const EXCEL_EXT = /\.(xlsx|xlsm)$/i;

export const isSupportedRmcplotsFile = (fileName: string): boolean =>
  XML_EXT.test(fileName) || EXCEL_EXT.test(fileName);

export const detectStageFromFilename = (filename: string): number | null => {
  const match = filename.match(/_stage(\d)/i);
  return match ? parseInt(match[1], 10) : null;
};

/**
 * Route a file to a reader by CONTENT first (zip signature = workbook, even
 * under a .rmcplots name), extension second. Legacy binary .xls is an OLE
 * file that exceljs cannot open — it is rejected with a clear message.
 */
export const detectRmcplotsSourceKind = async (file: File): Promise<RmcplotsSourceKind> => {
  const head = await readFileHead(file);
  const isZip = head.length >= 4 && head[0] === 0x50 && head[1] === 0x4b && head[2] === 0x03 && head[3] === 0x04;
  if (isZip) return 'excel';
  if (detectUploadFileKind(file.name, head) === 'workbook') {
    // OLE signature or a workbook extension without the zip signature
    throw new Error(`"${file.name}" is not an .xlsx workbook. Legacy .xls is not supported — save it as .xlsx and upload again.`);
  }
  if (EXCEL_EXT.test(file.name)) return 'excel';
  return 'xml';
};

const readFileAsText = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
};

const isAllZeroReading = (r: ParsedReading): boolean =>
  r.horizontalDisplacement === 0 && r.horizontalLoad === 0 &&
  r.verticalDisplacement === 0 && r.verticalLoad === 0;

/**
 * Extract the raw sample rows from the native .rmcplots XML: Item0-Item3 of
 * every <Sample> element (Item4 = target vertical force, ignored).
 */
export const readRmcplotsXmlRows = (text: string): RawRmcplotsRow[] => {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(text, 'application/xml');

  const parseError = xmlDoc.querySelector('parsererror');
  if (parseError) {
    throw new Error(`Invalid XML file: ${parseError.textContent}`);
  }

  const sampleElements = xmlDoc.getElementsByTagName('Sample');
  const rows: RawRmcplotsRow[] = [];
  for (let i = 0; i < sampleElements.length; i++) {
    const sample = sampleElements[i];
    const item0 = sample.getAttribute('Item0');
    const item1 = sample.getAttribute('Item1');
    const item2 = sample.getAttribute('Item2');
    const item3 = sample.getAttribute('Item3');
    if (item0 === null || item1 === null || item2 === null || item3 === null) continue;

    const intAttr = sample.getAttribute('Int');
    const intVal = intAttr ? parseInt(intAttr, 10) : i;
    rows.push({
      intIndex: intVal,
      reading: {
        sampleInt: intVal,
        horizontalDisplacement: parseFloat(item0),
        horizontalLoad: parseFloat(item1),
        verticalDisplacement: parseFloat(item2),
        verticalLoad: parseFloat(item3),
      },
    });
  }
  return rows;
};

/**
 * Shared cleanup applied to the raw rows of EITHER source format.
 *
 * 1. Classify leading rows where Item0 (Horizontal Displacement) <= 0
 *    — all-zero rows are machine initialization and are dropped; rows with
 *    load present are the pre-shear seating ramp and are kept flagged.
 * 2. Strip trailing rows where all 4 values are exactly 0
 *    — these are buffer padding from the machine
 * 3. Detect end-of-test: scan from midpoint and cut at the first row where:
 *    - Displacement jumps by more than 10× the median increment, OR
 *    - Vertical load drops below 50% of the median (machine unloaded), OR
 *    - Horizontal load goes negative (machine reversing)
 */
export const cleanRmcplotsRows = (
  allReadings: RawRmcplotsRow[],
  fileName: string,
  sourceKind: RmcplotsSourceKind,
): ParsedFileResult => {
  const totalRows = allReadings.length;

  // Step 1: Classify leading rows where Horizontal Displacement (Item0) <= 0.
  // Since 2026-07-17 (mig 108) these are no longer all dropped:
  //   - all-zero rows (no load data at all) are still dropped;
  //   - rows with load present are KEPT flagged isSeating — the machine's
  //     pre-shear seating ramp (shear load 0 → seating value). They feed the
  //     stress-path plot's 0-start and are excluded from calcs/analysis.
  let startIdx = 0;
  while (startIdx < allReadings.length && allReadings[startIdx].reading.horizontalDisplacement <= 0) {
    startIdx++;
  }
  const leadingRows = allReadings.slice(0, startIdx);
  const seatingRows = leadingRows.filter((r) => !isAllZeroReading(r.reading));
  seatingRows.forEach((r) => { r.reading.isSeating = true; });
  const leadingRowsSkipped = leadingRows.length - seatingRows.length;
  const seatingRowsKept = seatingRows.length;
  const startIntIndex = startIdx < allReadings.length ? allReadings[startIdx].intIndex : -1;

  // Step 2: Strip trailing all-zero rows
  let endIdx = allReadings.length - 1;
  while (endIdx >= startIdx && isAllZeroReading(allReadings[endIdx].reading)) {
    endIdx--;
  }
  const trailingZerosStripped = allReadings.length - 1 - endIdx;

  // Step 3: Detect machine retraction / end-of-test.
  // Unified rule: scan from the back half of the data and find the first row
  // where the vertical load drops by more than 50% compared to the median
  // vertical load. This catches all end-of-test scenarios:
  //   - Machine retraction (displacement jumps, loads drop)
  //   - Gradual unloading (load drops but displacement stays)
  //   - Mixed positive/negative horizontal load in retraction zone
  const endBeforeRetraction = endIdx;
  const validSlice = allReadings.slice(startIdx, endIdx + 1);
  if (validSlice.length > 20) {
    // Compute median vertical load from the middle 50% of data (stable test region)
    const q1 = Math.floor(validSlice.length * 0.25);
    const q3 = Math.floor(validSlice.length * 0.75);
    const midLoads = validSlice.slice(q1, q3).map((r) => Math.abs(r.reading.verticalLoad));
    midLoads.sort((a, b) => a - b);
    const medianVLoad = midLoads[Math.floor(midLoads.length / 2)];
    const vLoadThreshold = medianVLoad * 0.5; // 50% drop = end of test

    // Also compute displacement jump threshold
    const increments: number[] = [];
    for (let i = 1; i < validSlice.length; i++) {
      increments.push(Math.abs(
        validSlice[i].reading.horizontalDisplacement -
        validSlice[i - 1].reading.horizontalDisplacement,
      ));
    }
    const sortedInc = [...increments].sort((a, b) => a - b);
    const medianInc = sortedInc[Math.floor(sortedInc.length / 2)];
    const dispThreshold = Math.max(medianInc * 10, 0.1);

    // Scan from 50% onward: cut at first row that fails ANY check
    const searchFrom = Math.floor(validSlice.length * 0.5);
    for (let i = searchFrom; i < validSlice.length - 1; i++) {
      const nextRow = validSlice[i + 1].reading;
      const bigJump = increments[i] > dispThreshold;
      const loadDrop = Math.abs(nextRow.verticalLoad) < vLoadThreshold;
      const negLoad = nextRow.horizontalLoad < 0;

      if (bigJump || loadDrop || negLoad) {
        endIdx = startIdx + i; // keep up to this row
        break;
      }
    }
  }
  const trailingRetractionStripped = endBeforeRetraction - endIdx;

  // Seating rows first (they precede the shear phase by construction), then
  // the valid shear-phase slice.
  const readings = [
    ...seatingRows.map((r) => r.reading),
    ...allReadings.slice(startIdx, endIdx + 1).map((r) => r.reading),
  ];
  const stageNumber = detectStageFromFilename(fileName);

  return {
    stageNumber,
    readings,
    fileName,
    sourceKind,
    startIntIndex,
    totalRows,
    leadingRowsSkipped,
    seatingRowsKept,
    trailingZerosStripped,
    trailingRetractionStripped,
  };
};

/**
 * Parse an uploaded RMC150 result file — native `.rmcplots` XML or the same
 * data saved from Excel as `.xlsx` / `.xlsm` — and extract readings. The
 * stage comes from the filename (`_stage1` … `_stage5`) in both cases, and
 * both go through the identical cleanup in `cleanRmcplotsRows`.
 */
export const parseRmcplotsFile = async (file: File): Promise<ParsedFileResult> => {
  const sourceKind = await detectRmcplotsSourceKind(file);
  const rows = sourceKind === 'excel'
    ? await readRmcplotsExcelRows(file)
    : readRmcplotsXmlRows(await readFileAsText(file));
  return cleanRmcplotsRows(rows, file.name, sourceKind);
};
